import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { readRecentBoardIds } from '@/entities/board';
import {
  activeFilterCount,
  type CatalogFilters,
  type CatalogSort,
  matchesFilters,
} from '@/entities/catalog';
import type { AccessibleBoard } from '@/entities/collab';
import { EmptyState } from '@/shared/ui';

import { type BoardSort, sortBoards } from './board-sort';
import { BoardCard } from './Cards';
import styles from './HomeDashboard.module.css';
import { boardFilterable, catalogLink, HOME_OFFERED } from './home-filters';
import { SectionHead } from './SectionHead';

interface Props {
  boards: readonly AccessibleBoard[];
  /** The unified search text of the page. */
  query: string;
  filters: CatalogFilters;
  /** The order in force (the home offers opened / updated / name). */
  sort: CatalogSort;
  userId: string | null;
  locale: string;
}

/**
 * THE board catalog of the home page: one grid, narrowed by the page's shared search and filters
 * (the same ones the databases below and every workspace use) and ordered by the chosen sort. A
 * board appears at most once whatever grants provide it.
 */
export function BoardCatalog({ boards, query, filters, sort, userId, locale }: Props) {
  const { t } = useTranslation();
  const openedIds = useMemo(() => (userId ? readRecentBoardIds(userId) : []), [userId]);
  const shown = useMemo(
    () =>
      sortBoards(
        boards.filter((board) =>
          matchesFilters(
            boardFilterable(board, new Set(openedIds)),
            filters,
            userId,
            query,
            HOME_OFFERED,
          ),
        ),
        sort as BoardSort,
        openedIds,
        locale,
      ),
    [boards, filters, userId, query, sort, openedIds, locale],
  );
  const narrowed = query.trim() !== '' || activeFilterCount(filters, HOME_OFFERED) > 0;
  return (
    <section className={styles.catalog} aria-labelledby="home-catalog-title">
      <SectionHead
        id="home-catalog-title"
        title={t('home.catalog')}
        count={shown.length}
        to={catalogLink('/app/browse/boards', query)}
        linkLabel={t('home.allBoards')}
        linkTestId="all-boards-link"
      />
      {shown.length === 0 ? (
        <EmptyState title={narrowed ? t('home.nothingFound') : t('home.noBoards')} />
      ) : (
        <ul className={styles.grid} data-testid="board-catalog">
          {shown.map((board) => (
            <BoardCard key={board.id} board={board} />
          ))}
        </ul>
      )}
    </section>
  );
}
